import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { apiService } from '../services/api';
import { AdminUser } from '../types';

const AdminDashboardPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'suspended' | null>(null);
  const [suspendDialogVisible, setSuspendDialogVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [suspensionReason, setSuspensionReason] = useState('');
  const toast = React.useRef<Toast>(null);

  const statusOptions = [
    { label: 'All Users', value: null },
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Suspended', value: 'suspended' },
  ];

  useEffect(() => {
    loadUsers();
  }, [statusFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await apiService.listAllUsers(statusFilter || undefined);
      if (response.success && response.data) {
        setUsers(response.data.users);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.error || 'Failed to load users',
          life: 3000,
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load users',
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (user: AdminUser) => {
    confirmDialog({
      message: `Are you sure you want to approve ${user.name}?`,
      header: 'Confirm Approval',
      icon: 'pi pi-check-circle',
      accept: async () => {
        try {
          const response = await apiService.approveUser(user.user_id);
          if (response.success) {
            toast.current?.show({
              severity: 'success',
              summary: 'Success',
              detail: `${user.name} has been approved`,
              life: 3000,
            });
            loadUsers();
          } else {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: response.error || 'Failed to approve user',
              life: 3000,
            });
          }
        } catch (error) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to approve user',
            life: 3000,
          });
        }
      },
    });
  };

  const handleSuspend = (user: AdminUser) => {
    setSelectedUser(user);
    setSuspensionReason('');
    setSuspendDialogVisible(true);
  };

  const confirmSuspend = async () => {
    if (!selectedUser || !suspensionReason.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please provide a reason for suspension',
        life: 3000,
      });
      return;
    }

    try {
      const response = await apiService.suspendUser(selectedUser.user_id, suspensionReason);
      if (response.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `${selectedUser.name} has been suspended`,
          life: 3000,
        });
        setSuspendDialogVisible(false);
        setSelectedUser(null);
        setSuspensionReason('');
        loadUsers();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.error || 'Failed to suspend user',
          life: 3000,
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to suspend user',
        life: 3000,
      });
    }
  };

  const handleReactivate = (user: AdminUser) => {
    confirmDialog({
      message: `Are you sure you want to reactivate ${user.name}?`,
      header: 'Confirm Reactivation',
      icon: 'pi pi-refresh',
      accept: async () => {
        try {
          const response = await apiService.reactivateUser(user.user_id);
          if (response.success) {
            toast.current?.show({
              severity: 'success',
              summary: 'Success',
              detail: `${user.name} has been reactivated`,
              life: 3000,
            });
            loadUsers();
          } else {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: response.error || 'Failed to reactivate user',
              life: 3000,
            });
          }
        } catch (error) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to reactivate user',
            life: 3000,
          });
        }
      },
    });
  };

  const statusBodyTemplate = (rowData: AdminUser) => {
    const severity = rowData.status === 'approved' ? 'success' : rowData.status === 'pending' ? 'warning' : 'danger';
    return <Tag value={rowData.status.toUpperCase()} severity={severity} />;
  };

  const actionsBodyTemplate = (rowData: AdminUser) => {
    return (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {rowData.status === 'pending' && (
          <Button
            icon="pi pi-check"
            rounded
            text
            severity="success"
            tooltip="Approve"
            tooltipOptions={{ position: 'top' }}
            onClick={() => handleApprove(rowData)}
          />
        )}
        {rowData.status === 'approved' && (
          <Button
            icon="pi pi-ban"
            rounded
            text
            severity="danger"
            tooltip="Suspend"
            tooltipOptions={{ position: 'top' }}
            onClick={() => handleSuspend(rowData)}
          />
        )}
        {rowData.status === 'suspended' && (
          <Button
            icon="pi pi-refresh"
            rounded
            text
            severity="info"
            tooltip="Reactivate"
            tooltipOptions={{ position: 'top' }}
            onClick={() => handleReactivate(rowData)}
          />
        )}
      </div>
    );
  };

  const dateBodyTemplate = (rowData: AdminUser, field: keyof AdminUser) => {
    const date = rowData[field];
    if (!date) return '-';
    return new Date(date as string).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <h2 style={{ margin: 0 }}>User Management</h2>
        <Button
          icon="pi pi-refresh"
          rounded
          text
          onClick={loadUsers}
          tooltip="Refresh"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <Dropdown
          value={statusFilter}
          options={statusOptions}
          onChange={(e) => setStatusFilter(e.value)}
          placeholder="Filter by Status"
          style={{ width: '200px' }}
        />
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Search users..."
            style={{ width: '300px' }}
          />
        </span>
      </div>
    </div>
  );

  // Sort users: pending first, then approved, then suspended
  const sortedUsers = [...users].sort((a, b) => {
    const statusOrder = { pending: 0, approved: 1, suspended: 2 };
    return statusOrder[a.status] - statusOrder[b.status];
  });

  return (
    <div style={{ padding: '2rem' }}>
      <Toast ref={toast} />
      <ConfirmDialog />

      <DataTable
        value={sortedUsers}
        loading={loading}
        globalFilter={globalFilter}
        header={header}
        paginator
        rows={20}
        rowsPerPageOptions={[10, 20, 50]}
        emptyMessage="No users found"
        style={{ marginTop: '1rem' }}
      >
        <Column field="name" header="Name" sortable style={{ minWidth: '200px' }} />
        <Column field="email" header="Email" sortable style={{ minWidth: '250px' }} />
        <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ minWidth: '120px' }} />
        <Column
          field="created_at"
          header="Registered"
          body={(rowData) => dateBodyTemplate(rowData, 'created_at')}
          sortable
          style={{ minWidth: '150px' }}
        />
        <Column
          field="last_login"
          header="Last Login"
          body={(rowData) => dateBodyTemplate(rowData, 'last_login')}
          sortable
          style={{ minWidth: '150px' }}
        />
        <Column
          header="Actions"
          body={actionsBodyTemplate}
          style={{ minWidth: '120px', textAlign: 'center' }}
        />
      </DataTable>

      {/* Suspend Dialog */}
      <Dialog
        header={`Suspend User: ${selectedUser?.name}`}
        visible={suspendDialogVisible}
        style={{ width: '450px' }}
        onHide={() => {
          setSuspendDialogVisible(false);
          setSelectedUser(null);
          setSuspensionReason('');
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setSuspendDialogVisible(false);
                setSelectedUser(null);
                setSuspensionReason('');
              }}
              className="p-button-text"
            />
            <Button
              label="Suspend"
              icon="pi pi-ban"
              onClick={confirmSuspend}
              severity="danger"
              disabled={!suspensionReason.trim()}
            />
          </div>
        }
      >
        <div style={{ marginTop: '1rem' }}>
          <label htmlFor="suspension-reason" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
            Reason for Suspension *
          </label>
          <InputText
            id="suspension-reason"
            value={suspensionReason}
            onChange={(e) => setSuspensionReason(e.target.value)}
            placeholder="Enter reason for suspension"
            style={{ width: '100%' }}
          />
          <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280' }}>
            This reason will be recorded in the audit log.
          </small>
        </div>
      </Dialog>
    </div>
  );
};

export default AdminDashboardPage;
